/**
 * F-604-FE-02: NotificationBadge
 *
 * Bell icon button with a circular unread count badge.
 * Hidden when unread count is 0.
 * Shows "9+" when count > 9.
 * Clicking toggles the NotificationList dropdown.
 *
 * Used in DoctorLayout header.
 */

import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationList } from './NotificationList';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationBadge() {
  const [isOpen, setIsOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') setIsOpen(false);
  }

  const displayCount = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="
          relative
          inline-flex items-center justify-center
          min-h-[44px] min-w-[44px] px-2 rounded-lg
          text-clinical-charcoal hover:text-clinical-teal
          hover:bg-clinical-cream
          transition-colors duration-150
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2
        "
        aria-label={
          unreadCount === 0
            ? 'Notifications — no new notifications'
            : `Notifications — ${unreadCount} unread`
        }
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <BellIcon className="w-6 h-6" />

        {/* Badge — hidden when 0 */}
        {unreadCount > 0 && (
          <span
            className="
              absolute top-1.5 end-1.5
              inline-flex items-center justify-center
              min-w-[18px] h-[18px] px-1
              rounded-full
              bg-clinical-amber text-white
              text-[10px] font-bold leading-none
              pointer-events-none
            "
            aria-hidden="true"
          >
            {displayCount}
          </span>
        )}
      </button>

      {/* Notification list dropdown */}
      {isOpen && (
        <NotificationList onClose={() => setIsOpen(false)} />
      )}
    </div>
  );
}
